import { useEffect, useMemo, useRef, useState } from 'react';
import type { DeliveryBoardOrderDto, DeliveryStaffDto } from '@matix/api-client';
import { useLang } from '../../lib/language';
import { STATUS, customerOf, orderNo, staffName } from './model';

/**
 * "Scan driver with orders" — the legacy `driverOrdersLinkModal`.
 *
 * Scan (or type) the driver's code, optionally the chef's, then each order's
 * number; the list totals as it grows and one Confirm sends them all out with
 * that driver. A scanner is a keyboard that types fast and presses Enter, so
 * each field matches on every keystroke (exact code / order number, as the
 * legacy keyup did) and on Enter, then clears itself for the next scan.
 *
 * Only orders the legacy dialog accepted can be added: anything not waiting
 * for pickup, delivered or returned.
 */
export function LinkDriverDialog({
  orders,
  drivers,
  chefs,
  busy,
  money,
  onConfirm,
  onClose,
}: {
  orders: DeliveryBoardOrderDto[];
  drivers: DeliveryStaffDto[];
  chefs: DeliveryStaffDto[];
  busy: boolean;
  money: (n: number) => string;
  onConfirm: (driverId: number, chefId: number | null, orderIds: number[]) => void;
  onClose: () => void;
}): JSX.Element {
  const { t, lang } = useLang();
  const [driver, setDriver] = useState<DeliveryStaffDto | null>(null);
  const [chef, setChef] = useState<DeliveryStaffDto | null>(null);
  const [picked, setPicked] = useState<DeliveryBoardOrderDto[]>([]);
  const [driverText, setDriverText] = useState('');
  const [chefText, setChefText] = useState('');
  const [orderText, setOrderText] = useState('');
  const [miss, setMiss] = useState<string | null>(null);
  const driverRef = useRef<HTMLInputElement>(null);
  const chefRef = useRef<HTMLInputElement>(null);
  const orderRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    driverRef.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const eligible = useMemo(
    () => orders.filter((o) => o.status !== STATUS.Waiting && o.status !== STATUS.Delivered && o.status !== STATUS.Returned),
    [orders],
  );
  const total = picked.reduce((s, o) => s + o.amount, 0);

  // An exact match counts on a keystroke only when nothing LONGER still starts
  // with what has been typed — otherwise scanning code 12 would stop at 1, and
  // order #25546 at #2554. Enter (every scanner sends one) always decides.
  const byCode = (list: DeliveryStaffDto[], code: string, final: boolean) => {
    const s = code.trim().toLowerCase();
    if (!s) return [];
    const exact = list.filter((d) => (d.code ?? '').toLowerCase() === s);
    if (final) return exact;
    const longer = list.some((d) => { const c = (d.code ?? '').toLowerCase(); return c.length > s.length && c.startsWith(s); });
    return longer ? [] : exact;
  };

  function tryDriver(text: string, final: boolean): void {
    setDriverText(text);
    const hit = byCode(drivers, text, final);
    if (hit.length === 1) {
      setDriver(hit[0]!);
      setDriverText('');
      setMiss(null);
      (chefs.length > 0 && !chef ? chefRef : orderRef).current?.focus();
    } else if (final && text.trim()) {
      setMiss(t({ en: `No driver with code "${text.trim()}"`, ar: `لا يوجد سائق بالكود "${text.trim()}"` }));
      setDriverText('');
    }
  }

  function tryChef(text: string, final: boolean): void {
    setChefText(text);
    const hit = byCode(chefs, text, final);
    if (hit.length === 1) {
      setChef(hit[0]!);
      setChefText('');
      setMiss(null);
      orderRef.current?.focus();
    } else if (final && text.trim()) {
      setMiss(t({ en: `No chef with code "${text.trim()}"`, ar: `لا يوجد شيف بالكود "${text.trim()}"` }));
      setChefText('');
    }
  }

  function tryOrder(text: string, final: boolean): void {
    setOrderText(text);
    const s = text.trim().replace(/^#/, '');
    if (!s) return;
    const no = (o: DeliveryBoardOrderDto) => String(o.entry_no ?? o.id);
    const hit = eligible.filter((o) => no(o) === s);
    const longer = !final && orders.some((o) => no(o).length > s.length && no(o).startsWith(s));
    if (hit.length === 1 && !longer) {
      const o = hit[0]!;
      setPicked((prev) => (prev.some((p) => p.id === o.id) ? prev : [...prev, o]));
      setOrderText('');
      setMiss(null);
    } else if (final) {
      const exists = orders.some((o) => String(o.entry_no ?? o.id) === s);
      setMiss(
        exists
          ? t({ en: `Order #${s} is not open for a driver`, ar: `الطلب #${s} غير متاح للسائق` })
          : t({ en: `Order #${s} is not on this board`, ar: `الطلب #${s} غير موجود في هذه القائمة` }),
      );
      setOrderText('');
    }
  }

  return (
    <div className="cc-modal-backdrop" role="dialog" aria-modal="true" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div className="cc-modal dm-modal dm-link">
        <header className="cc-modal-head">
          <div>
            <div className="cc-modal-title">{t({ en: 'Link orders to driver', ar: 'ربط الطلبات بالسائق' })}</div>
            <div className="cc-modal-sub">
              {t({ en: 'Scan the driver, then each order', ar: 'امسح كود السائق ثم كل طلب' })}
            </div>
          </div>
          <button type="button" className="cc-drawer-close" onClick={onClose} aria-label={t({ en: 'Close', ar: 'إغلاق' })}>
            ×
          </button>
        </header>

        <div className="dm-scan-row">
          <input
            ref={driverRef}
            className="cc-input"
            value={driverText}
            placeholder={t({ en: 'Scan driver code', ar: 'امسح كود السائق' })}
            onChange={(e) => tryDriver(e.target.value, false)}
            onKeyDown={(e) => e.key === 'Enter' && tryDriver(driverText, true)}
          />
          <div className={`dm-scan-chip ${driver ? 'is-on' : ''}`}>
            {driver ? staffName(driver, lang) : t({ en: 'No driver', ar: 'بدون سائق' })}
            {driver && (
              <button type="button" onClick={() => setDriver(null)} aria-label={t({ en: 'Clear', ar: 'مسح' })}>
                ×
              </button>
            )}
          </div>
        </div>

        {chefs.length > 0 && (
          <div className="dm-scan-row">
            <input
              ref={chefRef}
              className="cc-input"
              value={chefText}
              placeholder={t({ en: 'Scan chef code', ar: 'امسح كود الشيف' })}
              onChange={(e) => tryChef(e.target.value, false)}
              onKeyDown={(e) => e.key === 'Enter' && tryChef(chefText, true)}
            />
            <div className={`dm-scan-chip ${chef ? 'is-on' : ''}`}>
              {chef ? staffName(chef, lang) : t({ en: 'No chef', ar: 'بدون شيف' })}
              {chef && (
                <button type="button" onClick={() => setChef(null)} aria-label={t({ en: 'Clear', ar: 'مسح' })}>
                  ×
                </button>
              )}
            </div>
          </div>
        )}

        <input
          ref={orderRef}
          className="cc-input"
          value={orderText}
          placeholder={t({ en: 'Scan order number', ar: 'امسح رقم الطلب' })}
          onChange={(e) => tryOrder(e.target.value, false)}
          onKeyDown={(e) => e.key === 'Enter' && tryOrder(orderText, true)}
        />
        {miss && <div className="dm-hint is-error">{miss}</div>}

        <ul className="dm-link-list">
          {picked.map((o) => (
            <li key={o.id}>
              <b>{orderNo(o)}</b>
              <span className="dm-link-cust">{customerOf(o, lang)}</span>
              <span className="dm-num">{money(o.amount)}</span>
              <button type="button" onClick={() => setPicked((p) => p.filter((x) => x.id !== o.id))} aria-label={t({ en: 'Remove', ar: 'حذف' })}>
                ×
              </button>
            </li>
          ))}
          {picked.length === 0 && (
            <li className="dm-empty-inline">{t({ en: 'No orders scanned yet', ar: 'لم يتم مسح أي طلب بعد' })}</li>
          )}
        </ul>

        <div className="dm-link-total">
          <span>
            {t({ en: 'Total', ar: 'الإجمالي' })} · {picked.length} {t({ en: 'orders', ar: 'طلبات' })}
          </span>
          <strong>{money(total)}</strong>
        </div>

        <div className="dm-modal-actions">
          <button type="button" className="cc-btn" onClick={onClose}>
            {t({ en: 'Cancel', ar: 'إلغاء' })}
          </button>
          <button
            type="button"
            className="cc-btn cc-btn--primary"
            disabled={!driver || picked.length === 0 || busy}
            title={
              !driver
                ? t({ en: 'You must select a driver', ar: 'يجب اختيار السائق' })
                : picked.length === 0
                  ? t({ en: 'You must select at least one order', ar: 'يجب اختيار طلب واحد على الأقل' })
                  : undefined
            }
            onClick={() => driver && onConfirm(driver.id, chef?.id ?? null, picked.map((o) => o.id))}
          >
            {busy ? t({ en: 'Saving…', ar: 'جارٍ الحفظ…' }) : t({ en: 'Confirm', ar: 'تأكيد' })}
          </button>
        </div>
      </div>
    </div>
  );
}
